import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  KibThumbdownFillIcon,
  KibThumbdownOutlineIcon,
  KibThumbupFillIcon,
  KibThumbupOutlineIcon,
} from '@chewy/kib-icons-react'

import type { AgentChatMessage } from '~/api/agentChat'
import RhapTextButton from '~/components/common/RhapTextButton'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'

import { MarkdownRenderer } from './MarkdownComponents'
import { MessageAttachment } from './MessageAttachment'
import { MessageBubble } from './MessageBubble'

import styles from './ChatMessage.module.scss'

interface AgentMessageBubbleProps {
  message: AgentChatMessage
  sessionId: string | null
}

enum FeedbackType {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

export const AgentMessageBubble: React.FC<AgentMessageBubbleProps> = ({
  message,
  sessionId,
}) => {
  const { t } = useTranslation('Agent')
  const [feedback, setFeedback] = useState<FeedbackType | null>(null)

  const handleFeedback = (type: FeedbackType) => {
    setFeedback(type)
    trackEvent(
      type === FeedbackType.POSITIVE
        ? TrackingEvent.AI_ASSISTANT_FEEDBACK_POSITIVE
        : TrackingEvent.AI_ASSISTANT_FEEDBACK_NEGATIVE,
      {
        messageId: message.id,
        messageContent: message.content,
        agent_session_id: sessionId,
        invocationId: message.invocationId,
      },
    )
  }

  return (
    <MessageBubble sender="agent">
      {message.isLoading ? (
        <div className={styles.loadingMessage}>
          <div className={styles.dots}>
            <div className={styles.dot} />
            <div className={styles.dot} />
            <div className={styles.dot} />
          </div>
        </div>
      ) : (
        <>
          <MarkdownRenderer content={message.content} sessionId={sessionId} />
          {message.attachments?.map((attachment, idx) => (
            <MessageAttachment
              attachment={attachment}
              // eslint-disable-next-line react/no-array-index-key
              key={`${message.id}-${attachment.mimeType}-${idx}`}
            />
          ))}
          <div className={styles.messageFooter}>
            <div className={styles.messageTimestamp}>{message.timestamp}</div>

            <div className={styles.feedbackContainer}>
              <RhapTextButton
                iconOnly
                plainHover
                aria-label={t('FEEDBACK_POSITIVE')}
                className={styles.feedbackButton}
                disabled={feedback !== null}
                emphasis="secondary"
                icon={
                  feedback === FeedbackType.POSITIVE ? (
                    <KibThumbupFillIcon />
                  ) : (
                    <KibThumbupOutlineIcon />
                  )
                }
                size="small"
                theme="utility"
                title={t('FEEDBACK_POSITIVE')}
                onClick={() => handleFeedback(FeedbackType.POSITIVE)}
              />
              <RhapTextButton
                iconOnly
                plainHover
                aria-label={t('FEEDBACK_NEGATIVE')}
                className={styles.feedbackButton}
                disabled={feedback !== null}
                emphasis="secondary"
                icon={
                  feedback === FeedbackType.NEGATIVE ? (
                    <KibThumbdownFillIcon />
                  ) : (
                    <KibThumbdownOutlineIcon />
                  )
                }
                size="small"
                theme="utility"
                title={t('FEEDBACK_NEGATIVE')}
                onClick={() => handleFeedback(FeedbackType.NEGATIVE)}
              />
            </div>
          </div>
        </>
      )}
    </MessageBubble>
  )
}

export default AgentMessageBubble
